import { brand } from "@/brand";

/**
 * Questions for the /faq page, the home page and the free-trial page. Plain
 * text only (no JSX): the build also writes them into the /faq page's
 * FAQPage structured data. Keep answers factual: they describe what the
 * platform does today.
 */
export interface FaqItem {
  q: string;
  a: string;
}

/** The free-trial switches (Admin → Plans & trials → Sign-up & trial): days, or null when off. */
export interface FaqTrials {
  member: number | null;
  ra: number | null;
}

/** No trial promised: the build's static head and structured data (the page follows the switches). */
export const NONE: FaqTrials = { member: null, ra: null };

/** The trial questions; `days` null words them without a number (the /free-trial page's static head). */
function trialItems(days: number | null): FaqItem[] {
  const n = days ? `${days}-day ` : "";
  return [
    {
      q: `How does the ${n}free trial work?`,
      a: `Every new account gets ${days ? `${days} days free` : "a free trial"}, with full access to every tool. The trial starts when you confirm your email at sign-up. After ${days ? `${days} days` : "the trial"}, take up the membership to carry on. The price is per month and includes GST. There are no hidden charges.`,
    },
    {
      q: "Do I need to pay to start the trial?",
      a: `No. Sign up, confirm your email and your ${n}free trial starts straight away. You pay only if you decide to continue after the trial.`,
    },
    {
      q: "What happens when my trial ends?",
      a: "Your access pauses until you take up the membership. Your account, settings, watchlists and alerts are kept, so you carry on where you left off once you subscribe.",
    },
  ];
}

const general = (member: number | null): FaqItem[] => [
  {
    q: "What does it cost?",
    a: `${member ? `Every new account starts with a ${member}-day free trial. After that, one` : "One"} membership covers every tool, at the price on the pricing page: per month, GST included, no hidden charges. Fees you agree directly with a Research Analyst are between you and the analyst.`,
  },
  {
    q: `Does ${brand.name} itself give investment advice?`,
    a: `No. ${brand.name} is a research technology platform: market data, research tools, and directories of SEBI-registered Research Analysts and Investment Advisers. It is not registered with SEBI as a Research Analyst or an Investment Adviser and does not recommend securities itself. Calls on the platform come only from SEBI-registered Research Analysts, under their own name and registration, to their own clients and followers. The tools help you do your own research; the decisions are yours.`,
  },
  {
    q: "Where does the market data come from?",
    a: "From NSE's public files (equity and F&O bhavcopies, official index closes, delivery data, participant open interest, FII/DII flows, company results and shareholding filings) and from broker market-data feeds for live prices. We check the sources against each other every day.",
  },
  {
    q: "How do I pay?",
    a: "Online, on the Payment page after you sign in: through our payment gateway (PayU), by UPI, card or net banking, whichever its secure page offers. Your membership starts as soon as the payment goes through, and we email you a receipt. We never see your card or bank details.",
  },
  {
    q: "Do you handle my money or place trades?",
    a: `No. ${brand.name} never holds client funds or securities and does not place orders. You trade through your own broker, if and when you decide to act.`,
  },
];

const TOOLS: FaqItem[] = [
  {
    q: "What does a member get?",
    a: "Every tool: Options Strategy Lab, Stock Strategy Lab, Market Atlas and Smart Money Tracker; the stock screener, option chain and F&O dashboards, options movers, backtests and signal research; price, idea and strategy-score alerts by email; watchlists; live market pages; the Research Analyst and Investment Adviser directories; and short “How it works” videos for each tool.",
  },
  {
    q: "How are backtests and results calculated?",
    a: "On NSE's official end-of-day prices, after typical Indian trading costs: brokerage, STT, exchange charges, GST, stamp duty and the SEBI fee. Past results show how a rule would have done; they are not a promise of future returns.",
  },
  {
    q: "What is a SEBI-registered research analyst?",
    a: "A research analyst (RA) is a person or firm registered with the Securities and Exchange Board of India under the Research Analysts Regulations, 2014, to publish research reports and buy/sell recommendations on securities. Registration numbers start with INH. Only registered RAs may charge for research recommendations.",
  },
  {
    q: "How do you check a Research Analyst's registration?",
    a: `${brand.name} imports SEBI's public register of research analysts every week. The directory lists only analysts on that register, with their registration number and validity, so you can check an analyst before you pay for research.`,
  },
  {
    q: "How do I follow a Research Analyst?",
    a: `Find the analyst on the home page or in the Research Analyst directory and open their profile. Members can Follow an analyst registered on ${brand.name} to get their public calls. Or tap Connect to ask to become their client: once the analyst accepts, all their calls come to you. Calls arrive in the app, as alerts and by email, at once or in a summary every two hours (Account, Notifications).`,
  },
];

/** For SEBI-registered Research Analysts (V063): the /for-research-analysts page and the FAQ. */
const raItems = ({ member, ra }: FaqTrials): FaqItem[] => [
  {
    q: `How does a Research Analyst join ${brand.name}?`,
    a: `Sign up as a Research Analyst with your SEBI registration number (INH...). We match it against SEBI's public register and email a one-time code to the address the register lists for you; entering it verifies your account. Already a member? Use "I'm a SEBI Research Analyst" on your Account page: your account and history are kept.`,
  },
  {
    q: "What do my clients pay?",
    a: `Nothing to ${brand.name}: a client you invite gets a free account with your calls and the basic market pages. Your fees are agreed and collected by you directly; we take no part in them. A client who also wants every research tool can add the membership themselves, and keeps receiving your calls.`,
  },
  {
    q: "What happens to the calls I publish?",
    a: "Each call is stored as published, with the time, the levels and the SEBI disclosure naming you, and can never be edited; a correction is a new, linked call. Results are worked out from market prices for every call the same way, so your track record is complete and verifiable.",
  },
  ra
    ? {
        q: "Is there a free trial for Research Analysts?",
        a: `Yes: ${ra} days free, once per person, from when you confirm your email. After it, you pay your plan's monthly price to continue. The pricing shows each plan's price and any launch offer.`,
      }
    : {
        q: "Is there a free trial for Research Analysts?",
        a: `No. Research Analyst plans start with the first payment. The pricing shows each plan's price and any launch offer.${member ? " Members (investors) keep their free trial." : ""}`,
      },
  {
    q: "Can I pay yearly?",
    a: "Yes: 12 months for the price of 10. Write to us and we activate the year once your payment is received.",
  },
];

/** The /faq page's groups, in page order (the free-trial group only while the member trial is on). */
export function faqGroups(t: FaqTrials): { id: string; title: string; items: FaqItem[] }[] {
  return [
    { id: "general", title: "General", items: general(t.member) },
    { id: "tools", title: "Research tools", items: TOOLS },
    ...(t.member ? [{ id: "free-trial", title: "Free trial", items: trialItems(t.member) }] : []),
    { id: "research-analysts", title: "For Research Analysts", items: raItems(t) },
  ];
}

/** Every question, flat. */
export function faqFor(t: FaqTrials): FaqItem[] {
  return faqGroups(t).flatMap((g) => g.items);
}

/** The four shown on the home page. */
export function topFaqFor(t: FaqTrials): FaqItem[] {
  const g = general(t.member);
  return [TOOLS[0], t.member ? trialItems(t.member)[0] : g[2], g[0], g[1]].filter((f): f is FaqItem => !!f);
}

/** The trial questions, for the free-trial page. */
export function trialFaqFor(days: number | null): FaqItem[] {
  return trialItems(days);
}

/** The Research Analyst questions. */
export function raFaqFor(t: FaqTrials): FaqItem[] {
  return raItems(t);
}

// The build's static versions (structured data in the prerendered HTML): no
// trial is promised there, since the switches can change after a build.
export const FAQ_GROUPS = faqGroups(NONE);
export const FAQ: FaqItem[] = faqFor(NONE);
export const TOP_FAQ: FaqItem[] = topFaqFor(NONE);
export const TRIAL_FAQ: FaqItem[] = trialFaqFor(null);
export const RA_FAQ: FaqItem[] = raFaqFor(NONE);

const OPERATOR = brand.legalName && brand.legalName !== brand.name ? `, operated by ${brand.legalName},` : "";

/**
 * Questions for the /services page (and its FAQPage structured data). Not in
 * FAQ_GROUPS: they are about our technology services, not the platform.
 */
export const servicesFaqFor = (member: number | null): FaqItem[] => [
  {
    q: `Is ${brand.name} registered with SEBI?`,
    a: `No. ${brand.name}${OPERATOR} is a technology platform. It is not registered with SEBI as a Research Analyst or an Investment Adviser and does not give investment advice. Research Analysts and Investment Advisers listed in our directories are independently registered with SEBI and are responsible for their own research and advice.`,
  },
  {
    q: "What do the research and analytics APIs include?",
    a: "Research derived from our data: screener results, technical and fundamental scores, F&O analytics such as put-call ratio, max pain and open-interest build-up, and the directories of SEBI-registered Research Analysts and Investment Advisers. Raw exchange price feeds (real-time or end-of-day) are available on request only, subject to exchange licensing.",
  },
  {
    q: "How are services priced?",
    a: `The membership price is on the pricing page, per month and including GST${member ? `, with a ${member}-day free trial` : ""}. Apps, APIs and custom research tools are quoted in writing once we understand what you need.`,
  },
  {
    q: "Do you help with SEBI registration, KYC or compliance?",
    a: "No. We provide technology only. For registration, KYC, compliance and legal questions, please consult a qualified professional.",
  },
  {
    q: "Do you handle client money or place trades?",
    a: `No. ${brand.name} never holds client funds or securities and does not place orders, on its own platform or in anything we build for you.`,
  },
];

export const SERVICES_FAQ: FaqItem[] = servicesFaqFor(null);
